import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    TextFilter,
    Pagination,
    CollectionPreferences,
    Flashbar,
    Modal,
    Alert,
    FormField,
    Input,
} from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Constants from './constants';
import { listAllIAMRoles, offboardIAMRole, setIAMRoleStatus } from './apis';
import {
    STATUS_ACTIVE,
    STATUS_INACTIVE,
    apiErrorMessage,
    isOffboardInterrupted,
    roleStatus,
    withStatus,
} from './utils';
import { EmptyState, getFilterCounterText } from '../../lab-management/group/utils';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { displayErrorContent, displaySuccessContent, isAdmin } from '../../lib/utils';
import { SERVER_ERROR } from '../../lib/constants';

export const ADMIN_ONLY_HINT = 'Only Device Farm admins can disable or enable an IAM role.';
const CONTACT_DEV_TEAM = 'Try again later or contact our dev team @ds2-device-farm-dev for more details.';

/**
 * The delegated role's name, the last segment of its identity_id (adf-iam-auth-<team>).
 * @param {object} role - a DDFCallerRegistry item
 */
const roleNameOf = (role) => role.identity_id.split('/').pop();

export default function IAMRoles() {
    /**
     * IAM roles component: the AWS IAM roles onboarded for Device Farm IAM auth
     */
    const history = useHistory();
    const [tableLoading, setTableLoading] = React.useState(false);
    const [roles, setRoles] = React.useState([]);
    const [selectedRoles, setSelectedRoles] = React.useState([]);
    const [isDdfAdmin, setIsDdfAdmin] = React.useState(false);
    // load failures, cleared by the next successful load
    const [errorMessage, setErrorMessage] = React.useState([]);
    // outcome of the last offboard / disable / enable, which a reload must not clear
    const [actionMessage, setActionMessage] = React.useState([]);
    const [offboardVisible, setOffboardVisible] = React.useState(false);
    const [offboardConfirmText, setOffboardConfirmText] = React.useState('');
    const [offboardLoading, setOffboardLoading] = React.useState(false);
    const [disableVisible, setDisableVisible] = React.useState(false);
    const [statusLoading, setStatusLoading] = React.useState(false);
    // identifies the newest load request, so superseded responses can be dropped
    const latestRequestId = React.useRef(0);
    const mounted = React.useRef(true);
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: Constants.IAM_ROLE_VISIBLE_COLUMNS,
    });
    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(roles, {
        filtering: {
            noMatch: (
                <EmptyState
                    title="No matches"
                    subtitle="We can’t find a match."
                    action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                />
            ),
        },
        pagination: { pageSize: preferences.pageSize },
        sorting: {
            defaultState: { sortingColumn: Constants.SORTED_FILED, isDescending: true },
        },
    });

    // What the selected role allows. The backend enforces the same rules; these only keep a
    // user from starting a request that is bound to be refused.
    const selected = selectedRoles[0];
    const selectedActive = !!selected && roleStatus(selected) === STATUS_ACTIVE;
    const configureDisabled = !selected || !(selectedActive || isDdfAdmin);
    const offboardDisabled = !selected || !(selectedActive || isDdfAdmin || isOffboardInterrupted(selected));
    const statusDisabled = !selected || !isDdfAdmin;
    const statusLabel = selected && !selectedActive ? 'Enable' : 'Disable';

    /**
     * load IAM role table
     */
    const loadTable = () => {
        // only the newest request may write state: a response that a later refresh (or unmount)
        // has superseded is dropped, so the table never falls back to an older result
        const requestId = ++latestRequestId.current;
        const superseded = () => requestId !== latestRequestId.current;
        (async function () {
            setTableLoading(true);
            try {
                const response = await listAllIAMRoles();
                if (superseded()) {
                    return;
                }
                const loaded = response['response'].map(withStatus);
                setRoles(loaded);
                // keep the selection on the reloaded copy of the same role, or drop it if gone
                setSelectedRoles((previous) =>
                    loaded.filter((role) => previous.some((p) => p.identity_id === role.identity_id))
                );
                setErrorMessage([]);
            } catch (e) {
                console.error('Fail to load IAM role(s) with error', e);
                if (superseded()) {
                    return;
                }
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to load the onboarded IAM roles. Contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
            setTableLoading(false);
        })();
    };

    /**
     * process configure button click event
     */
    const onConfigureClick = () => {
        history.push(`/api-auth/iam-roles/configure/${encodeURIComponent(selected.identity_id)}`);
    };

    const openOffboard = () => {
        setOffboardConfirmText('');
        setOffboardVisible(true);
    };

    /**
     * offboard the selected role once the user confirmed it in the modal
     */
    const onConfirmOffboard = async () => {
        const role = selected;
        setOffboardLoading(true);
        try {
            await offboardIAMRole(role.identity_id);
            displaySuccessContent(
                `Successfully offboarded ${role.team} (${roleNameOf(role)}) from Amazon Device Farm.`,
                setActionMessage
            );
        } catch (e) {
            console.error('Fail to offboard the selected IAM role with error', e);
            // a partial offboard names the step it stopped at; the role stays and can be retried
            displayErrorContent(
                `Fail to offboard ${role.team}`,
                apiErrorMessage(e) || CONTACT_DEV_TEAM,
                setActionMessage
            );
        }
        setOffboardLoading(false);
        setOffboardVisible(false);
        loadTable();
    };

    /**
     * disable (INACTIVE) or enable (ACTIVE) the selected role
     * @param {string} desired - ACTIVE or INACTIVE
     */
    const changeStatus = async (desired) => {
        const role = selected;
        const verb = desired === STATUS_INACTIVE ? 'disable' : 'enable';
        setStatusLoading(true);
        try {
            await setIAMRoleStatus(role.identity_id, desired);
            displaySuccessContent(
                desired === STATUS_INACTIVE
                    ? `Disabled ${role.team}. Its callers can no longer get tokens, and outstanding tokens were revoked.`
                    : `Enabled ${role.team}. Its callers can get tokens again.`,
                setActionMessage
            );
        } catch (e) {
            console.error(`Fail to ${verb} the selected IAM role with error`, e);
            displayErrorContent(
                `Fail to ${verb} ${role.team}`,
                apiErrorMessage(e) || CONTACT_DEV_TEAM,
                setActionMessage
            );
        }
        setStatusLoading(false);
        setDisableVisible(false);
        loadTable();
    };

    /**
     * process disable / enable button click event: disable asks for confirmation, enable does not
     */
    const onStatusClick = () => {
        if (selectedActive) {
            setDisableVisible(true);
        } else {
            changeStatus(STATUS_ACTIVE);
        }
    };

    useEffect(() => {
        // initialize the component
        mounted.current = true;
        loadTable();
        // gates the admin-only action; a failed check leaves it disabled, the backend decides anyway
        isAdmin()
            .then((response) => mounted.current && setIsDdfAdmin(!!(response && response.status)))
            .catch((e) => console.error('Fail to check whether the user is a Device Farm admin', e));
        // bumping the counter on unmount supersedes any in-flight request
        return () => {
            mounted.current = false;
            latestRequestId.current += 1;
        };
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'IAM roles', href: '#/api-auth/iam-roles' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description={
                    <>
                        IAM roles let a team call Amazon Device Farm APIs with its own AWS credentials instead of a
                        personal token or a bot account. Each onboarded team gets a Device Farm role that its trusted
                        callers assume, then exchange for a short-lived API token. Identity is team-level, like a bot
                        account. For questions about onboarding, configuring or offboarding an IAM role, contact our dev
                        team @ds2-device-farm-dev.
                    </>
                }
            >
                IAM roles
            </Header>
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => setSelectedRoles(detail.selectedItems)}
                selectedItems={selectedRoles}
                selectionType="single"
                trackBy="identity_id"
                header={
                    <Header
                        counter={`(${roles.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadTable} iconName="refresh" variant="icon" ariaLabel="Refresh" />
                                <Button
                                    disabled={statusDisabled}
                                    disabledReason={isDdfAdmin ? undefined : ADMIN_ONLY_HINT}
                                    loading={statusLoading}
                                    onClick={onStatusClick}
                                >
                                    {statusLabel}
                                </Button>
                                <Button disabled={offboardDisabled} onClick={openOffboard}>
                                    Offboard
                                </Button>
                                <Button disabled={configureDisabled} onClick={onConfigureClick}>
                                    Configure
                                </Button>
                                <Button href="#/api-auth/iam-roles/onboard" variant="primary">
                                    Onboard
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        ADF IAM roles
                    </Header>
                }
                visibleColumns={preferences.visibleContent}
                columnDefinitions={Constants.IAM_ROLE_COLUMNS_DEFINITION}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>No IAM roles to display</b>
                    </Box>
                }
                loading={tableLoading}
                loadingText="Loading IAM roles"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />
            <Flashbar items={errorMessage} />
            <Flashbar items={actionMessage} />
            {selected && (
                <Modal
                    visible={offboardVisible}
                    onDismiss={() => setOffboardVisible(false)}
                    header="Offboard IAM role"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setOffboardVisible(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={offboardLoading}
                                    disabled={offboardConfirmText !== selected.team}
                                    onClick={onConfirmOffboard}
                                >
                                    Offboard
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        <Box>
                            Permanently offboard <b>{selected.team}</b>?
                        </Box>
                        <Alert type="warning">
                            This action cannot be undone. The IAM role <b>{roleNameOf(selected)}</b> and its policy, the
                            Device Farm identity, its private-group memberships and all outstanding tokens will be
                            removed.
                        </Alert>
                        <FormField label={`To confirm, type the team name "${selected.team}".`}>
                            <Input
                                value={offboardConfirmText}
                                placeholder={selected.team}
                                ariaLabel="Team name to confirm offboarding"
                                onChange={({ detail }) => setOffboardConfirmText(detail.value)}
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>
            )}
            {selected && (
                <Modal
                    visible={disableVisible}
                    onDismiss={() => setDisableVisible(false)}
                    header="Disable IAM role"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDisableVisible(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={statusLoading}
                                    onClick={() => changeStatus(STATUS_INACTIVE)}
                                >
                                    Disable
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        <Box>
                            Disable <b>{selected.team}</b>?
                        </Box>
                        <Box>
                            Its trusted callers can no longer get tokens, and outstanding tokens are revoked
                            immediately. Nothing is deleted, and a Device Farm admin can enable it again.
                        </Box>
                    </SpaceBetween>
                </Modal>
            )}
        </SpaceBetween>
    );
}
